import { useState, useEffect } from 'react'
import ListingComponent from '../../../Molecule/ListingComponent'
import CONSTANTS from '../../../../Constants'
import { selectAllProps } from '../../../../Models'
import { ADVANCE_SEARCH } from '../../../../Constants/ApiDefinations'
import mainApiService from '../../../../Services'
import { showAlert } from '../../../../Store/Slice/alert.slice'
import { combineErrorMessage, getDateInFormat } from '../../../../Helpers/Util'
import { useAppDispatch } from '../../../../Store/Hooks'
import AssetStateLabel from '../../../Labels/AssetStateLabel'
import MUICheckbox from '../../../Shared/MUI-Checkbox'
import HTTP_STATUS_CODE from '../../../../Constants/HttpStatusCode'

interface SearchList {
  searchPayload: any
  getSelectedRecords: (record: any, dataCount: number) => void
}

const BulkSearchResult = ({ searchPayload, getSelectedRecords }: SearchList) => {
  const [listData, setListData] = useState<any[]>([])
  const [dataCount, setDataCount] = useState<number>(0)
  const [isLoader, setIsLoader] = useState<boolean>(false)
  const [selectAllProps, setSelectAllProps] = useState<selectAllProps>({
    checked: false,
    showCheckbox: false,
    isCheckboxClicked: false,
  })
  const [checkedState, setCheckedState] = useState<{ [key: string]: any }>({})
  const [allSelectedRecords, setAllSelectedRecords] = useState<any[]>([])
  const [pageLimit, setPageLimit] = useState<number>(0)
  const [resetFlag, setResetFlag] = useState(false)

  const dispatch = useAppDispatch()

  useEffect(() => {
    setCheckedState({})
    setAllSelectedRecords([])
    setResetFlag(true)
  }, [searchPayload])

  useEffect(() => {
    if (resetFlag) {
      setListData([])
      getBulkStateChangeSearchFunction({})
      setResetFlag(false)
    }
  }, [resetFlag])

  useEffect(() => {
    const allChecked =
      listData.length > 0 && listData.every((row) => Boolean(checkedState[String(row?._id)]))
    let checkedRecords: any[] = []
    checkedRecords = Object.values({ ...checkedState })
      .filter((item) => item?._id && item?.contentId)
      .map((item) => ({
        contentId: item?.['contentId'],
        contentDocId: item?.['_id'],
        additionalData: {
          title: item?.['title'],
          layout: item?.['layout'],
          type: item?.['type'],
        },
      }))
    getSelectedRecords(checkedRecords, dataCount)
    setAllSelectedRecords(checkedRecords)
    setSelectAllProps((prev) => ({
      ...prev,
      showCheckbox: listData.length > 0,
      checked: allChecked,
      disabled: checkedRecords.length >= CONSTANTS.MAX_BULK_SELECT_RECORD && !allChecked,
    }))
  }, [listData, checkedState])

  useEffect(() => {
    updateAllCheckboxes()
  }, [checkedState.length, listData.length, allSelectedRecords.length])

  useEffect(() => {
    if (selectAllProps.isCheckboxClicked === false) return

    const checked = selectAllProps.checked
    if (checked) {
      handleSelectAllChange(checked)
    }
  }, [pageLimit])

  const renderRowWithCheckbox = (row: any, id: number) => {
    const isChecked = Boolean(checkedState[String(row?._id)])
    const isDisabled = allSelectedRecords.length >= CONSTANTS.MAX_BULK_SELECT_RECORD && !isChecked
    return {
      ...row,
      checkbox: (
        <MUICheckbox
          dataTestId={'checkbox-input-' + id}
          id={String(row?._id)}
          checked={isChecked}
          handleChange={() => handleSelectChange(row)}
          disabled={isDisabled}
          size='small'
          sx={{ padding: 0 }}
        />
      ),
    }
  }

  const updateAllCheckboxes = () => {
    setListData((prevListData) => prevListData.map((row, id) => renderRowWithCheckbox(row, id)))
  }

  const handleSelectChange = (item: any) => {
    setCheckedState((prev) => {
      const id = String(item?._id)
      const isChecked = Boolean(prev[id])
      let updated: { [key: string]: any }
      if (isChecked) {
        updated = { ...prev }
        delete updated[id]
      } else {
        if (Object.keys(prev).length >= CONSTANTS.MAX_BULK_SELECT_RECORD) {
          return prev
        }
        updated = { ...prev, [id]: item }
      }
      const allChecked =
        listData.length > 0 && listData.every((row) => Boolean(updated[String(row?._id)]))
      setSelectAllProps((prevProps) => ({
        ...prevProps,
        checked: allChecked,
        isCheckboxClicked: false,
      }))
      return updated
    })
  }

  const getBulkStateChangeSearchFunction = async (options?: any) => {
    if (
      !searchPayload ||
      typeof searchPayload !== 'object' ||
      (typeof searchPayload === 'object' && Array.isArray(searchPayload))
    )
      return
    if (Object.keys(searchPayload).length === 0) return
    const payload = searchPayload
    const cleanedObj = Object.fromEntries(
      Object.entries(options).filter(
        ([key, value]) => value !== '' && value != null && value !== 0,
      ),
    )
    const combinedPayload = { ...payload, ...cleanedObj }
    if (!Object.hasOwn(cleanedObj, 'limit')) {
      combinedPayload.limit = payload.limit
    }
    if (!Object.hasOwn(cleanedObj, 'page')) {
      combinedPayload.page = payload.page
    }
    setIsLoader(true)
    const requestedData = ADVANCE_SEARCH.ADVANCE_SEARCH_RESULT(combinedPayload)
    const result = await mainApiService(requestedData)
    setPageLimit(options?.limit ?? payload.limit)
    if (result?.responseCode === HTTP_STATUS_CODE.OK) {
      const tempData: any = []
      const newCheckedState: { [key: string]: any } = { ...checkedState }
      result?.data?.records.forEach((element: any, key: any) => {
        const id = String(element?._id)
        const isChecked = Boolean(checkedState[id])
        const isDisabled =
          allSelectedRecords.length >= CONSTANTS.MAX_BULK_SELECT_RECORD && !isChecked
        tempData.push({
          checkbox: (
            <MUICheckbox
              dataTestId={'checkbox-input-' + key}
              id={id}
              checked={isChecked}
              handleChange={() => handleSelectChange(element)}
              disabled={isDisabled}
              size='small'
              sx={{ padding: 0 }}
            />
          ),
          _id: id,
          contentId: element?.contentId,
          title: element?.title,
          layout: element?.layout,
          type: element?.type,
          state: <AssetStateLabel state={element?.stateDisplayName} />,
          segment: element?.segment,
          createdAt: getDateInFormat(element?.createdAt, CONSTANTS.DATE_FORMAT_TIME_SECONDS),
          updatedAt: getDateInFormat(element?.updatedAt, CONSTANTS.DATE_FORMAT_TIME_SECONDS),
          lastFeaturedDate: getDateInFormat(
            element?.lastFeaturedDate,
            CONSTANTS.DATE_FORMAT_TIME_SECONDS,
          ),
        })
      })
      setCheckedState(newCheckedState)
      setListData(tempData)
      setDataCount(result?.data?.count)
      setIsLoader(false)
    } else {
      dispatch(showAlert([true, combineErrorMessage(result), 'error']))
      setIsLoader(false)
    }
  }

  const handleSelectAllChange = (checked: boolean) => {
    const newCheckedState: { [key: string]: any } = { ...checkedState }
    const alreadySelectedIds = new Set(Object.keys(newCheckedState))
    let canSelect = CONSTANTS.MAX_BULK_SELECT_RECORD - alreadySelectedIds.size

    if (checked) {
      for (const row of listData) {
        const id = String(row._id)
        if (!newCheckedState[id] && canSelect > 0) {
          newCheckedState[id] = row
          canSelect--
        }
      }
    } else {
      listData.forEach((row) => {
        delete newCheckedState[String(row._id)]
      })
    }
    setCheckedState(newCheckedState)
    setSelectAllProps((prev) => ({
      ...prev,
      checked,
      isCheckboxClicked: true,
    }))
  }

  return (
    <div className='mt-20 bulk-operations-listing'>
      <ListingComponent
        noPadding
        columns={CONSTANTS.BULK_STATE_CHANGE_SEARCH_LIST_HEADER}
        tableRows={listData}
        dataCount={dataCount}
        showCreateButton={false}
        showSearchField={false}
        isLoading={isLoader}
        searchFunction={(search, sorttype, coltype, filter, pagenumber, pagecount) => {
          getBulkStateChangeSearchFunction({
            sortBy: sorttype && coltype,
            filterCondition: search ? 'contains' : filter.filterBY,
            searchText: search,
            filterBy: search ? 'title' : filter.filterText,
            sortType: sorttype,
            page: pagenumber,
            limit: pagecount,
          })
        }}
        initialCheckedState={Object.fromEntries(
          Object.entries(checkedState).map(([id, obj]) => [id, Boolean(obj)]),
        )}
        selectAllProps={selectAllProps}
        onSelectAllChange={handleSelectAllChange}
        paginationOption={CONSTANTS.PAGINATION_OPTIONS}
        entityType='advanceSearch'
      />
    </div>
  )
}

export default BulkSearchResult
